import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Globe, AlertCircle, Info, ChevronDown, ChevronUp, ArrowRight, Phone, Wifi } from 'lucide-react'
import SEOHead from '@/components/SEOHead'

const dailyRates = [
  {
    zone: 'Go Roam in Europe (EU)',
    rate: '£2.50',
    description: 'EU destinations — activates your UK allowance for 24 hours',
    color: 'from-blue-500 to-blue-600',
    free: false,
    dataNote: 'Up to 12GB data/month',
    callsNote: 'Calls & texts: no monthly limit',
  },
  {
    zone: 'Go Roam in Europe (Non-EU)',
    rate: '£2.90',
    description: 'Iceland, Norway, Switzerland & other non-EU European destinations',
    color: 'from-indigo-500 to-indigo-600',
    free: false,
    dataNote: 'Up to 12GB data/month',
    callsNote: 'Calls & texts: no monthly limit',
  },
  {
    zone: 'Go Roam Around the World',
    rate: '£6.20',
    description: 'USA, Australia, New Zealand & 20+ popular destinations',
    color: 'from-emerald-500 to-emerald-600',
    free: false,
    dataNote: 'Up to 12GB data/month',
    callsNote: 'Up to 3,000 mins & 5,000 texts/month to UK',
  },
  {
    zone: 'Go Roam Around the World Extra',
    rate: '£8.90',
    description: 'Canada, Japan, India, UAE & 90+ more destinations',
    color: 'from-amber-500 to-amber-600',
    free: false,
    dataNote: 'Up to 12GB data/month',
    callsNote: '⚠ Data only — calls & texts not available',
    dataOnly: true,
  },
  {
    zone: 'Republic of Ireland & Isle of Man',
    rate: 'Varies',
    description: 'Roaming is an extra cost and varies from package to package',
    color: 'from-green-500 to-green-600',
    free: true,
    dataNote: 'Up to 12GB data/month (FUP applies)',
    callsNote: 'Calls & texts: no monthly limit',
  },
]

const surchargeRates = [
  { label: 'EU destinations — Calls', value: '2.25p/min (RRP 2.5p/min)' },
  { label: 'EU destinations — SMS', value: '1.48p/SMS (RRP 1.64p/SMS)' },
  { label: 'EU destinations — Data', value: '0.23p/MB (RRP 0.25p/MB)' },
  { label: 'Non-EU / World / World Extra — Calls', value: '2.7p/min (RRP 3p/min)' },
  { label: 'Non-EU / World / World Extra — SMS', value: '1.8p/SMS (RRP 2p/SMS)' },
  { label: 'Non-EU / World / World Extra — Data', value: '0.27p/MB (RRP 0.3p/MB)' },
]

export default function ThreeGoRoamRates() {
  const [surchargeOpen, setSurchargeOpen] = useState(false)

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Three Go Roam Rates & Info | Silux Mobile"
        description="Use your allowances abroad with Three Go Roam. See included countries, daily roaming rates, fair-use data limits and how to roam in 70+ destinations with Silux Mobile. Roaming is an extra cost and varies from package to package."
        canonical="https://www.siluxmobile.co.uk/three-go-roam/rates"
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0a3d41] via-[#0F5257] to-[#0a3d41] text-white relative overflow-hidden">
        {/* Subtle dot grid background */}
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />

        <div className="max-w-5xl mx-auto px-4 py-14 flex flex-col lg:flex-row items-center gap-10 relative z-10">
          {/* Left: branding + content */}
          <div className="flex-1 text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl font-bold mb-3 leading-tight">Three Go Roaming</h1>
            <p className="text-xl text-white/80 mb-2 font-medium">Rates &amp; Information</p>
            <p className="text-white/60 text-sm max-w-md mb-8">
              Roaming is an extra cost and varies from package to package — Go Roam rates start from £2.50/day. Powered by Three UK's Go Roam service.
            </p>
            <Link
              to="/three-go-roam/destinations"
              className="inline-flex items-center gap-2 bg-white text-[#0F5257] font-semibold px-6 py-3 rounded-full hover:bg-white/90 transition-colors text-sm shadow-lg"
            >
              View All Destinations <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Right: SIM card hero image */}
          <div className="flex-shrink-0 flex items-center justify-center">
            <img
              src="/images/sim-card-three.png"
              alt="Three SIM Card"
              className="h-52 sm:h-64 w-auto drop-shadow-2xl"
              style={{ filter: 'drop-shadow(0 20px 50px rgba(0,0,0,0.5))', transform: 'rotate(8deg)' }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12 space-y-12">

        {/* How it works */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">How Go Roam Works</h2>
          <p className="text-gray-500 text-sm mb-6">Simple daily charging — no surprises</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Arrive at your destination', body: 'Make sure data roaming is switched on in your device settings before using your phone.' },
              { step: '2', title: 'Use your phone as normal', body: 'The daily charge triggers automatically the first time you use any service — calls, texts, or data.' },
              { step: '3', title: 'Your UK allowance for 24 hrs', body: 'Once charged, your UK voice, text and data allowance is unlocked for 24 hours, subject to roaming FUP limits.' },
            ].map((s) => (
              <div key={s.step} className="flex gap-4">
                <div className="w-9 h-9 rounded-full bg-[#0F5257] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {s.step}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm mb-1">{s.title}</p>
                  <p className="text-gray-500 text-sm">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl flex gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              In the <strong>Republic of Ireland</strong> and <strong>Isle of Man</strong>, roaming is an extra cost and varies from package to package — check the roaming charges for your plan before you travel (fair use policy applies).
            </p>
          </div>
        </div>

        {/* Daily Rates */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Daily Rates</h2>
          <p className="text-gray-500 text-sm mb-6">Per SIM, per day — charge applies only when you use your phone</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {dailyRates.map((r) => (
              <div
                key={r.zone}
                className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden ${r.free ? 'ring-2 ring-green-400' : ''}`}
              >
                <div className={`bg-gradient-to-r ${r.color} p-4`}>
                  <div className="flex justify-between items-start">
                    <p className="text-white font-semibold text-sm leading-tight pr-2">{r.zone}</p>
                    <span className="text-white font-bold text-xl whitespace-nowrap">{r.rate}</span>
                  </div>
                  {r.free ? (
                    <span className="inline-flex items-center gap-1 mt-2 bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                      Varies by package
                    </span>
                  ) : (
                    <span className="inline-flex items-center mt-2 bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                      per SIM / per day
                    </span>
                  )}
                </div>
                <div className="p-4 space-y-2">
                  <p className="text-gray-500 text-sm">{r.description}</p>
                  <div className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 rounded-lg px-2.5 py-1.5">
                    <Wifi className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{r.dataNote}</span>
                  </div>
                  <div className={`flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 ${r.dataOnly ? 'text-amber-700 bg-amber-50' : 'text-purple-700 bg-purple-50'}`}>
                    <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{r.callsNote}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Allowances & Fair Use — always visible */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-bold text-gray-900">Roaming Allowances &amp; Fair Use Policy</h2>
            <p className="text-gray-500 text-sm mt-0.5">Go Roam is designed for short-term travel — monthly limits apply</p>
          </div>
          <div className="p-6 space-y-5">
            <p className="text-sm text-gray-600">
              Go Roam is intended for UK residents visiting destinations for <strong>short periods</strong> such as holidays or business trips.
              It is not designed for people who live abroad or stay for extended periods.
            </p>

            {/* Data */}
            <div className="border border-blue-200 bg-blue-50 rounded-xl p-5">
              <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                <Wifi className="w-4 h-4" /> Data
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-700 font-medium">Monthly roaming data cap</span>
                  <span className="font-bold text-gray-900">12GB per month</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">After 12GB — EU destinations</span>
                  <span className="font-semibold text-gray-900">0.36p per MB</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">After 12GB — Non-EU / World / World Extra</span>
                  <span className="font-semibold text-gray-900">0.39p per MB</span>
                </div>
              </div>
              <p className="mt-3 text-xs text-blue-700 italic">
                Once you exceed 12GB the daily roaming charge no longer applies — only the per-MB rate above.
              </p>
            </div>

            {/* Calls & SMS */}
            <div className="border border-purple-200 bg-purple-50 rounded-xl p-5">
              <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                <Phone className="w-4 h-4" /> Calls &amp; Texts
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Go Roam in Europe — Calls</span>
                  <span className="font-semibold text-gray-900">No monthly limit</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Go Roam in Europe — Texts</span>
                  <span className="font-semibold text-gray-900">No monthly limit</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Around the World — Calls to UK numbers</span>
                  <span className="font-semibold text-gray-900">Up to 3,000 mins/month</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Around the World — Texts to UK numbers</span>
                  <span className="font-semibold text-gray-900">Up to 5,000 texts/month</span>
                </div>
                <div className="flex justify-between gap-4 pt-1 border-t border-purple-200">
                  <span className="text-gray-600">Around the World Extra — Calls &amp; Texts</span>
                  <span className="font-semibold text-amber-700">Not available (data only)</span>
                </div>
              </div>
              <p className="mt-3 text-xs text-purple-700 italic">
                Calls and texts apply to standard UK landlines and mobile numbers only.
              </p>
            </div>
          </div>
        </div>

        {/* Spend limit */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-4">
          <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900 mb-1">Worldwide Data Roaming Spend Limit</p>
            <p className="text-amber-800 text-sm">
              A default spend limit of <strong>£25.00 per SIM</strong> is applied when activating roaming.
              Alerts are triggered at <strong>80%</strong> and <strong>100%</strong> of the limit — the SIM is barred from further roaming charges when 100% is reached.
              This limit can be changed or increased on request.
            </p>
          </div>
        </div>

        {/* Extended Stay Surcharge — collapsible */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <button
            className="w-full flex justify-between items-center p-6 text-left hover:bg-gray-50 transition-colors"
            onClick={() => setSurchargeOpen(!surchargeOpen)}
          >
            <div>
              <h2 className="text-xl font-bold text-gray-900">Extended Stay Surcharges</h2>
              <p className="text-gray-500 text-sm mt-0.5">Applies when roaming for 2+ complete months in a rolling 12-month period</p>
            </div>
            {surchargeOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          {surchargeOpen && (
            <div className="px-6 pb-6 border-t border-gray-100">
              <p className="text-sm text-gray-600 py-4">
                If customers roam exclusively in Go Roam destinations for <strong>any two complete months</strong> in a rolling 12-month period,
                Silux Mobile reserves the right to apply the following surcharges (only when the monthly 12GB roaming data cap has not been reached):
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Service</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-700">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surchargeRates.map((r, i) => (
                      <tr key={r.label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 text-gray-700">{r.label}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">{r.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-4 italic">
                If any time is spent in a destination not included in Go Roam during the month abroad, this fair use policy and surcharge will not apply.
              </p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#0F5257] to-[#1a7a82] rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-2">Ready to Roam?</h3>
          <p className="text-white/70 mb-6 text-sm">Get a Three SIM from Silux Mobile and take your UK allowance with you</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/three-go-roam/destinations"
              className="inline-flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white font-medium px-6 py-3 rounded-full transition-colors text-sm"
            >
              <Globe className="w-4 h-4" /> View Destinations
            </Link>
            <Link
              to="/sim-only/roaming"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0F5257] font-semibold px-6 py-3 rounded-full hover:bg-white/90 transition-colors text-sm"
            >
              Shop Three SIMs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

      </div>
    </div>
  )
}
